import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useParams } from 'react-router';
import { getActivePets, getPet, getPetPrescriptions } from 'api/petApi';
import { PetInterface } from 'types/pet';
import FetchError from 'types/http';
import PageLoader from 'components/loaders/PageLoader';
import { ACCOUNT_OVERVIEW_PATH } from '../util/config';
import {
  ApiPetPrescription,
  ApiPetProfileStatusEnum,
} from '../api/generated-client/PetProfile-Service-A.v1';
import PetProfileView from '../components/pet-profile-view/PetProfileView';
import { handleAuthError } from '../util/auth';
import { retryCount } from '../util/reactQueryOptions';

export default function ViewPet() {
  const [activePet, setActivePet] = useState(false);

  const { petId } = useParams<{ petId: string }>();

  const petProfileQuery = useQuery<PetInterface, FetchError>([getPet.key, petId], getPet, {
    retry: retryCount,
  });

  const { data: prescriptions, isLoading: prescriptionsLoading } = useQuery<
    ApiPetPrescription[],
    FetchError
  >([getPetPrescriptions.key, petId], getPetPrescriptions, {
    retry: retryCount,
  });

  const petProfile = petProfileQuery.data;
  const petProfileStatus = petProfileQuery.status;
  const petProfileLoading = petProfileQuery.isLoading;
  const { data, isLoading, error } = useQuery<PetInterface[], FetchError>(
    [getActivePets.key],
    getActivePets,
    {
      retry: retryCount,
    },
  );
  const location = useLocation();
  const hasAuthError = error ? handleAuthError(error.status, location.pathname) : false;

  useEffect(() => {
    // NOTE: If pet is removed in Memorial Flow and browser back is clicked, pet object will be undefined. Redirect to accounts page in this case.
    const inactivePet = petProfile?.status === ApiPetProfileStatusEnum.Inactive;
    if (!petProfileLoading && inactivePet) {
      window.location.href = ACCOUNT_OVERVIEW_PATH;
    } else if (!petProfileLoading && !petProfile) {
      window.location.href = ACCOUNT_OVERVIEW_PATH;
    } else setActivePet(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petId, petProfile]);

  if (petProfile && activePet) {
    return !data ? (
      <PetProfileView
        petProfile={petProfile}
        pets={[petProfile]}
        petPrescriptions={prescriptions}
      />
    ) : (
      <PetProfileView petProfile={petProfile} pets={data} petPrescriptions={prescriptions} />
    );
  }

  if (
    !activePet ||
    isLoading ||
    prescriptionsLoading ||
    petProfileStatus === 'idle' ||
    petProfileStatus === 'loading' ||
    hasAuthError
  ) {
    return (
      <div className="kib-margin__top" style={{ display: 'flex' }} data-testid="view-pet-loader">
        <PageLoader data-testid="view-pet-loader-icon" />
      </div>
    );
  }
  throw new Error('Unable to load Pet Profile View Page. Failed to load pet profile');
}
